import React, { useState, useEffect } from "react";
import { Building2, Plus, FileText, Trash2, Landmark, ChevronRight, BookOpen } from "lucide-react";
import { toast } from "sonner";
import DatePicker from "@/components/ui/DatePicker";
import api, { money, errMsg } from "../lib/api";
import { todayIST } from "../lib/datetime";
import useAutoRefresh from "../lib/useAutoRefresh";
import CustomSelect from "../components/ui/CustomSelect";

export default function VendorMatrixPage() {
  const [activeTab, setActiveTab] = useState("directory"); // "directory" | "ledger"
  const [matrix, setMatrix] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState("");

  // Vendor Ledger state
  const [vendorLedger, setVendorLedger] = useState(null);
  const [ledgerOpenVendor, setLedgerOpenVendor] = useState("");
  const [assigningLine, setAssigningLine] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [bankId, setBankId] = useState("");

  // Vendor form
  const [name, setName] = useState("");
  const [leadTime, setLeadTime] = useState(4);
  const [contact, setContact] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [gstNumber, setGstNumber] = useState("");
  const [notes, setNotes] = useState("");

  // Bank details. The beneficiary account number is what a bank statement
  // narration prints, so it is the key Bank Reconciliation uses to auto-map a
  // statement debit to this vendor's purchase invoices. Shared by the "add"
  // and "edit" modals — each resets it on open.
  const [bankVendor, setBankVendor] = useState(null); // vendor whose a/c we're editing
  const [bankForm, setBankForm] = useState({ bank_name: "", bank_account_no: "", bank_ifsc: "" });

  const openAddVendor = () => {
    setBankForm({ bank_name: "", bank_account_no: "", bank_ifsc: "" });
    setShowVendorModal(true);
  };

  const openBankDetails = (v) => {
    setBankVendor(v);
    setBankForm({
      bank_name: v.bank_name || "",
      bank_account_no: v.bank_account_no || "",
      bank_ifsc: v.bank_ifsc || "",
    });
  };

  const handleSaveBank = async (e) => {
    e.preventDefault();
    if (!bankVendor) return;
    try {
      await api.put(`/vendors/${bankVendor.id}`, bankForm);
      toast.success(`Bank details saved for ${bankVendor.name}`);
      setBankVendor(null);
      fetchMatrix();
    } catch (err) {
      toast.error(errMsg(err) || "Failed to save bank details");
    }
  };

  // Contract form
  const [contractNumber, setContractNumber] = useState("");
  const [startDate, setStartDate] = useState(todayIST());
  const [endDate, setEndDate] = useState("");
  const [terms, setTerms] = useState("");

  const fetchAccounts = async () => {
    try {
      const res = await api.get("/banking/accounts");
      const accts = res.data?.accounts || [];
      setAccounts(accts);
      if (!bankId && accts.length > 0) setBankId(accts[0].id);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchVendorLedger = async (silent = false) => {
    try {
      const res = await api.get(`/banking/vendor-ledger${bankId ? `?bank_id=${bankId}` : ""}`);
      setVendorLedger(res.data);
    } catch (e) {
      if (!silent) toast.error(errMsg(e));
      else console.error(e);
    }
  };

  const clearAssignment = async (lineId) => {
    setAssigningLine(lineId);
    try {
      await api.post("/banking/assign-line", { line_id: lineId, vendor: "" });
      toast.success("Back to automatic matching");
      await fetchVendorLedger(true);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setAssigningLine("");
    }
  };

  useEffect(() => {
    fetchMatrix();
    fetchAccounts();
  }, []);

  useEffect(() => {
    fetchVendorLedger();
  }, [bankId]);

  const fetchMatrix = async () => {
    try {
      const res = await api.get("/vendors/matrix");
      setMatrix(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Vendor performance and statement matches move as bills and entries are posted; refresh silently.
  useAutoRefresh(() => {
    fetchMatrix();
    fetchVendorLedger(true);
  }, 30000);

  // Only vendors supplying the current inventory belong on this scorecard.
  // The register also holds legacy names with zero linked SKUs (old POs);
  // showing them here is what put unwanted vendors on the matrix.
  const visible = matrix.filter((v) => (v.sku_count || 0) > 0);

  const handleCreateVendor = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Please enter vendor name");
      return;
    }
    try {
      await api.post("/vendors", {
        name,
        lead_time_days: parseInt(leadTime),
        contact,
        email,
        phone,
        gst_number: gstNumber,
        bank_name: bankForm.bank_name,
        bank_account_no: bankForm.bank_account_no,
        bank_ifsc: bankForm.bank_ifsc,
      });
      toast.success("Saved vendor profile!");
      setShowVendorModal(false);
      setName("");
      setBankForm({ bank_name: "", bank_account_no: "", bank_ifsc: "" });
      fetchMatrix();
    } catch (err) {
      toast.error(errMsg(err) || "Failed to save vendor");
    }
  };

  const handleDeleteVendor = async (id, vendorName) => {
    if (!window.confirm(`Delete vendor "${vendorName}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/vendors/${id}`);
      toast.success(`Vendor "${vendorName}" deleted`);
      fetchMatrix();
    } catch (err) {
      toast.error(errMsg(err) || "Failed to delete vendor");
    }
  };

  const handleCreateContract = async (e) => {
    e.preventDefault();
    if (!selectedVendorId || !endDate) {
      toast.error("Please select vendor and end date");
      return;
    }
    try {
      await api.post(`/vendors/${selectedVendorId}/contracts`, {
        contract_number: contractNumber,
        start_date: startDate,
        end_date: endDate,
        terms,
      });
      toast.success("Contract created!");
      setShowContractModal(false);
      fetchMatrix();
    } catch (err) {
      toast.error(errMsg(err) || "Failed to create contract");
    }
  };

  if (loading) return (
    <div className="p-6 sm:p-10 max-w-7xl mx-auto space-y-8 animate-pulse">
      <div className="h-10 bg-slate-200 rounded w-1/3 mb-4"></div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1, 2, 3].map(i => <div key={i} className="h-48 bg-slate-200 rounded-xl"></div>)}
      </div>
      <div className="h-96 bg-slate-200 rounded-xl w-full"></div>
    </div>
  );

  return (
    <div className="p-6 sm:p-10 max-w-7xl mx-auto space-y-8 animate-in fade-in duration-300">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="lss-overline">Supplier Network</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 flex items-center gap-3 mt-1">
            <Building2 className="w-8 h-8 text-slate-900" /> Vendor Performance & Contract Matrix
          </h1>
          <p className="text-slate-600 font-medium text-sm mt-1">
            Supplier lead-time tracking, contract SLAs, and fulfillment reliability scorecards
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={openAddVendor}
            className="flex items-center gap-2 bg-slate-900 text-white font-bold px-5 py-2.5 rounded-lg hover:bg-slate-800 transition shadow-sm text-sm"
          >
            <Plus className="w-4 h-4" /> Add Vendor Profile
          </button>
        </div>
      </div>

      {/* Navigation Tabs: Directory vs Ledger */}
      <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
        <button
          onClick={() => setActiveTab("directory")}
          className={`px-5 py-2.5 text-xs font-extrabold uppercase tracking-wider rounded-xl transition-all shrink-0 ${
            activeTab === "directory"
              ? "bg-slate-950 text-amber-300 shadow-md"
              : "text-slate-700 hover:text-slate-950 hover:bg-slate-100"
          }`}
        >
          Vendor Directory &amp; Matrix
        </button>
        <button
          onClick={() => setActiveTab("ledger")}
          className={`px-5 py-2.5 text-xs font-extrabold uppercase tracking-wider rounded-xl transition-all shrink-0 ${
            activeTab === "ledger"
              ? "bg-slate-950 text-amber-300 shadow-md"
              : "text-slate-700 hover:text-slate-950 hover:bg-slate-100"
          }`}
        >
          Vendor Ledger ({vendorLedger?.summary?.vendors || 0})
        </button>
      </div>

      {activeTab === "directory" && (
      <>
      {/* Vendor Scorecard Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {visible.map((v) => (
          <div key={v.id} className="lss-card p-6 space-y-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-xl font-extrabold text-slate-900">{v.name}</h3>
                <div className="text-xs text-slate-600 font-semibold mt-0.5">{v.contact || "No contact specified"}</div>
                <div className="text-xs font-semibold mt-0.5 flex items-center gap-1.5 text-slate-600">
                  <Landmark className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  {v.bank_account_no ? (
                    <span className="tabular">A/c {v.bank_account_no}{v.bank_ifsc ? ` · ${v.bank_ifsc}` : ""}</span>
                  ) : (
                    <span className="text-slate-400 italic">No bank a/c saved</span>
                  )}
                </div>
              </div>
              <span className="lss-badge bg-amber-100 text-amber-900 border border-amber-300 font-bold">
                {v.lead_time_days} days lead
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-200 text-xs">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5">
                <span className="text-slate-600 font-semibold block">Supplied SKUs</span>
                <span className="text-slate-900 font-extrabold text-base mt-0.5 block">{v.sku_count || 0} Products</span>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5">
                <span className="text-slate-600 font-semibold block">Purchase Orders</span>
                <span className="text-slate-900 font-extrabold text-base mt-0.5 block">{v.total_pos || 0} POs</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => openBankDetails(v)}
                title="Bank details used for statement auto-matching"
                className="flex-1 min-w-[95px] py-2.5 text-xs font-bold bg-slate-900 text-white hover:bg-slate-800 rounded-md transition flex items-center justify-center gap-1.5"
              >
                <Landmark className="w-3.5 h-3.5" /> Bank Details
              </button>
              <button
                onClick={() => {
                  setLedgerOpenVendor(v.name);
                  setActiveTab("ledger");
                }}
                title="View vendor ledger"
                className="flex-1 min-w-[75px] py-2.5 text-xs font-bold bg-amber-100 hover:bg-amber-200 text-amber-950 rounded-md border border-amber-300 transition flex items-center justify-center gap-1.5"
              >
                <BookOpen className="w-3.5 h-3.5" /> Ledger
              </button>
              <button
                onClick={() => {
                  setSelectedVendorId(v.id);
                  setShowContractModal(true);
                }}
                className="flex-1 min-w-[85px] py-2.5 text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-md border border-slate-300 transition flex items-center justify-center gap-1.5"
              >
                <FileText className="w-3.5 h-3.5" /> Attach SLA
              </button>
              <button
                onClick={() => handleDeleteVendor(v.id, v.name)}
                title="Delete vendor"
                className="py-2.5 px-3 text-xs font-bold bg-red-50 hover:bg-red-100 text-red-700 rounded-md border border-red-200 transition flex items-center justify-center"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Vendor Master Matrix Table */}
      <div className="lss-card p-6">
        <h2 className="text-lg font-bold text-slate-900 mb-4">Vendor Master Directory</h2>
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="lss-table">
            <thead>
              <tr>
                <th>Vendor Name</th>
                <th>Lead Time</th>
                <th>Contact</th>
                <th>Bank A/c</th>
                <th>Supplied SKUs</th>
                <th>Total Orders</th>
                <th className="text-right">Reliability Rating</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((v) => (
                <tr key={v.id}>
                  <td className="font-bold text-slate-900">{v.name}</td>
                  <td className="text-slate-700 font-medium">{v.lead_time_days} days</td>
                  <td className="text-slate-700 font-medium">{v.contact || "-"}</td>
                  <td className="text-slate-700 font-medium tabular">{v.bank_account_no || "-"}</td>
                  <td className="text-slate-900 font-bold">{v.sku_count}</td>
                  <td className="text-slate-900 font-bold">{v.total_pos}</td>
                  <td className="text-right text-amber-800 font-extrabold">
                    {v.reliability_rating ? `${v.reliability_rating}/5` : "A+ Verified"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}

      {activeTab === "ledger" && (
        <div className="space-y-6">
          {/* Summary KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="lss-card p-4">
              <span className="lss-overline block">Vendors Tracked</span>
              <span className="text-2xl font-extrabold text-slate-950 tabular">
                {vendorLedger?.summary?.vendors || 0}
              </span>
            </div>
            <div className="lss-card p-4">
              <span className="lss-overline block">Total Billed</span>
              <span className="text-2xl font-extrabold text-slate-950 tabular">
                {money(vendorLedger?.summary?.billed || 0)}
              </span>
            </div>
            <div className="lss-card p-4">
              <span className="lss-overline block">Paid from Bank</span>
              <span className="text-2xl font-extrabold text-emerald-800 tabular">
                {money(vendorLedger?.summary?.paid || 0)}
              </span>
            </div>
            <div className="lss-card p-4">
              <span className="lss-overline block">Outstanding Balance</span>
              <span className={`text-2xl font-extrabold tabular ${(vendorLedger?.summary?.balance || 0) > 0 ? "text-rose-700" : "text-emerald-800"}`}>
                {money(vendorLedger?.summary?.balance || 0)}
              </span>
            </div>
          </div>

          {/* Account Filter */}
          {accounts.length > 1 && (
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs font-bold text-slate-600">Bank Statement Feed:</span>
              <div className="w-64">
                <CustomSelect
                  options={[
                    { value: "", label: "All / Primary Account" },
                    ...accounts.map((a) => ({
                      value: a.id,
                      label: `${a.bank_name || a.name} (•••• ${a.account_no?.slice(-4) || ""})`,
                    })),
                  ]}
                  value={bankId}
                  onChange={(val) => setBankId(val)}
                  placeholder="Select bank account"
                />
              </div>
            </div>
          )}

          {/* Vendor Ledger Card */}
          <div className="lss-card overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/70 flex flex-wrap items-center justify-between gap-3">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                Vendor Ledger — bills in · bank payments out ({vendorLedger?.summary?.vendors || 0})
              </span>
              <span className="text-xs text-slate-500 font-semibold flex flex-wrap items-center gap-x-3 gap-y-1">
                <span>Billed <b className="text-slate-900 tabular">{money(vendorLedger?.summary?.billed || 0)}</b></span>
                <span>·</span>
                <span>Paid <b className="text-emerald-700 tabular">{money(vendorLedger?.summary?.paid || 0)}</b></span>
                <span>·</span>
                <span className={(vendorLedger?.summary?.balance || 0) > 0 ? "text-rose-700" : "text-emerald-700"}>
                  Outstanding <b className="tabular">{money(vendorLedger?.summary?.balance || 0)}</b>
                </span>
              </span>
            </div>
            <div className="px-6 py-3 border-b border-slate-200 bg-amber-50/70 text-[11px] font-semibold text-amber-900 leading-relaxed">
              Every bill and every statement debit that paid it, in date order, with the balance still owed after each entry.
              Each payment shows how it was mapped — bank a/c no, vendor name, or assigned by hand; clearing a manual mapping
              returns the line to automatic matching.
              <b> Click a vendor row to open its ledger.</b>
            </div>
            <div className="overflow-auto max-h-[560px]">
              <table className="lss-table min-w-[900px]">
                <thead>
                  <tr>
                    <th>Vendor</th>
                    <th className="text-right">Entries</th>
                    <th className="text-right">Billed ₹</th>
                    <th className="text-right">Paid from bank ₹</th>
                    <th className="text-right">Outstanding ₹</th>
                    <th>Bank a/c</th>
                  </tr>
                </thead>
                <tbody>
                  {(vendorLedger?.vendors || []).map((v) => (
                    <React.Fragment key={v.vendor}>
                      <tr
                        className="hover:bg-slate-50/70 cursor-pointer"
                        onClick={() => setLedgerOpenVendor((o) => (o === v.vendor ? "" : v.vendor))}
                        data-testid={`vl-vendor-${v.vendor}`}
                      >
                        <td>
                          <span className="flex items-center gap-2">
                            <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform shrink-0 ${ledgerOpenVendor === v.vendor ? "rotate-90" : ""}`} />
                            <span className="text-xs font-extrabold uppercase tracking-wide text-slate-950">{v.vendor}</span>
                            {v.assigned > 0 && (
                              <span className="lss-badge text-[10px] bg-amber-100 text-amber-900">{v.assigned} assigned</span>
                            )}
                          </span>
                        </td>
                        <td className="text-right tabular text-xs text-slate-600">{v.bills} bill · {v.payments} paid</td>
                        <td className="text-right tabular font-bold">{money(v.billed)}</td>
                        <td className="text-right tabular font-bold text-emerald-700">{money(v.paid)}</td>
                        <td className={`text-right tabular font-extrabold ${v.balance > 0 ? "text-rose-700" : "text-emerald-700"}`}>{money(v.balance)}</td>
                        <td className="text-[11px] text-slate-500 font-semibold whitespace-nowrap">
                          {v.bank_account_no ? `A/c ${v.bank_account_no}${v.bank_ifsc ? ` · ${v.bank_ifsc}` : ""}` : "no a/c saved"}
                        </td>
                      </tr>
                      {ledgerOpenVendor === v.vendor && (
                        <tr>
                          <td colSpan={6} className="p-0">
                            <table className="w-full border-t border-slate-200">
                              <tbody>
                                {(v.entries || []).map((e) => (
                                  <tr key={`${e.kind}-${e.id}`} className="bg-slate-50/80 hover:bg-slate-100/70">
                                    <td className="px-4 py-1.5 tabular text-[11px] font-bold text-slate-700 whitespace-nowrap w-[92px]">{e.date || "—"}</td>
                                    <td className="px-2 py-1.5 w-[86px]">
                                      <span className={`px-2 py-0.5 rounded-lg border text-[10px] font-extrabold uppercase tracking-wide whitespace-nowrap ${e.kind === "bill" ? "bg-sky-50 text-sky-800 border-sky-200" : "bg-emerald-50 text-emerald-800 border-emerald-200"}`}>
                                        {e.kind === "bill" ? "Bill" : "Payment"}
                                      </span>
                                    </td>
                                    <td className="px-2 py-1.5 text-[11px] text-slate-700">
                                      {e.ref && <span className="font-bold text-slate-900">{e.ref} · </span>}
                                      <span className="text-slate-500">{(e.narration || "").replace(/\s+/g, " ")}</span>
                                    </td>
                                    <td className={`px-2 py-1.5 text-right tabular text-[11px] font-extrabold whitespace-nowrap ${e.kind === "bill" ? "text-slate-900" : "text-emerald-700"}`}>
                                      {e.kind === "bill" ? "+" : "−"}{money(e.amount)}
                                    </td>
                                    <td className={`px-2 py-1.5 text-right tabular text-[11px] font-black whitespace-nowrap ${e.balance > 0 ? "text-rose-700" : "text-emerald-700"}`}>
                                      {money(e.balance)}
                                    </td>
                                    <td className="px-3 py-1.5">
                                      {e.kind === "bill" ? (
                                        <span className="text-[10px] font-bold uppercase text-slate-400">purchase invoice</span>
                                      ) : (
                                        <span className="flex items-center justify-between gap-3">
                                          <span className="text-[10px] font-bold uppercase text-amber-800 whitespace-nowrap">
                                            {e.matched_by === "manual" ? "assigned by hand" : e.matched_by === "account" ? "bank a/c no" : "vendor name"}
                                          </span>
                                          {e.matched_by === "manual" && (
                                            <button
                                              type="button"
                                              onClick={(ev) => { ev.stopPropagation(); clearAssignment(e.id); }}
                                              disabled={assigningLine === e.id}
                                              className="text-[10px] font-bold text-slate-500 hover:text-rose-700 underline whitespace-nowrap"
                                              title="Remove the manual mapping and let automatic matching decide again"
                                            >
                                              {assigningLine === e.id ? "…" : "use auto-match"}
                                            </button>
                                          )}
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {(vendorLedger?.vendors || []).length === 0 && (
                    <tr><td colSpan={6} className="text-center text-slate-500 font-medium py-8 text-xs">No bills or vendor payments yet.</td></tr>
                  )}
                </tbody>
                {(vendorLedger?.vendors || []).length > 0 && (
                  <tfoot>
                    <tr className="bg-amber-50/70 border-t border-amber-200/80">
                      <td className="px-3 py-2.5 text-xs font-extrabold uppercase tracking-wide text-amber-950">Total</td>
                      <td />
                      <td className="text-right tabular font-black text-slate-950">{money(vendorLedger?.summary?.billed || 0)}</td>
                      <td className="text-right tabular font-black text-slate-950">{money(vendorLedger?.summary?.paid || 0)}</td>
                      <td className={`text-right tabular font-black ${(vendorLedger?.summary?.balance || 0) > 0 ? "text-rose-700" : "text-emerald-700"}`}>
                        {money(vendorLedger?.summary?.balance || 0)}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Add Vendor Modal */}
      {showVendorModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50" onClick={() => setShowVendorModal(false)}>
          <div className="bg-white border border-slate-200 shadow-xl rounded-xl p-6 max-w-md w-full space-y-4 text-slate-900" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-extrabold text-slate-900">Add New Vendor Profile</h3>
            <form onSubmit={handleCreateVendor} className="space-y-3">
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Vendor Name</label>
                <input
                  type="text"
                  placeholder="e.g. L'Oreal India Ltd"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="lss-input w-full"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Fulfillment Lead Time (Days)</label>
                <input
                  type="number"
                  value={leadTime}
                  onChange={(e) => setLeadTime(e.target.value)}
                  className="lss-input w-full"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Contact Person</label>
                <input
                  type="text"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  className="lss-input w-full"
                />
              </div>
              <div className="pt-3 border-t border-slate-200 space-y-2">
                <div className="text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                  Bank details — auto-matches bank statement debits to this vendor's bills
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="Bank name"
                    value={bankForm.bank_name}
                    onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })}
                    className="lss-input w-full"
                  />
                  <input
                    type="text"
                    placeholder="IFSC"
                    value={bankForm.bank_ifsc}
                    onChange={(e) => setBankForm({ ...bankForm, bank_ifsc: e.target.value })}
                    className="lss-input w-full"
                  />
                </div>
                <input
                  type="text"
                  placeholder="Account number"
                  value={bankForm.bank_account_no}
                  onChange={(e) => setBankForm({ ...bankForm, bank_account_no: e.target.value })}
                  className="lss-input w-full"
                  data-testid="vendor-bank-account"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowVendorModal(false)}
                  className="lss-btn-outline px-4 py-2 text-xs font-bold uppercase tracking-wider"
                >
                  Cancel
                </button>
                <button type="submit" className="lss-btn-gold px-4 py-2 text-xs font-bold uppercase tracking-wider">
                  Save Vendor
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bank Details Modal — the a/c number Bank Reconciliation auto-matches on */}
      {bankVendor && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50" onClick={() => setBankVendor(null)}>
          <div className="bg-white border border-slate-200 shadow-xl rounded-xl p-6 max-w-md w-full space-y-4 text-slate-900" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 border-b border-slate-200 pb-3">
              <Landmark className="w-5 h-5 text-amber-700" />
              <h3 className="text-xl font-extrabold text-slate-900">Bank Details — {bankVendor.name}</h3>
            </div>
            <form onSubmit={handleSaveBank} className="space-y-3">
              <p className="text-xs font-semibold text-slate-500 leading-relaxed">
                The account number appears in the bank statement narration, so it is what maps this
                vendor's statement debits to their purchase invoices in Bank Reconciliation.
              </p>
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Account Number</label>
                <input
                  type="text"
                  value={bankForm.bank_account_no}
                  onChange={(e) => setBankForm({ ...bankForm, bank_account_no: e.target.value })}
                  placeholder="e.g. 085705002233"
                  className="lss-input w-full tabular"
                  data-testid="vendor-bank-account"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Bank Name</label>
                  <input
                    type="text"
                    value={bankForm.bank_name}
                    onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })}
                    placeholder="e.g. ICICI Bank"
                    className="lss-input w-full"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">IFSC</label>
                  <input
                    type="text"
                    value={bankForm.bank_ifsc}
                    onChange={(e) => setBankForm({ ...bankForm, bank_ifsc: e.target.value })}
                    placeholder="e.g. ICIC0000857"
                    className="lss-input w-full"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setBankVendor(null)}
                  className="lss-btn-outline px-4 py-2 text-xs font-bold uppercase tracking-wider"
                >
                  Cancel
                </button>
                <button type="submit" className="lss-btn-gold px-4 py-2 text-xs font-bold uppercase tracking-wider">
                  Save Bank Details
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Contract Modal */}
      {showContractModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50" onClick={() => setShowContractModal(false)}>
          <div className="bg-white border border-slate-200 shadow-xl rounded-xl p-6 max-w-md w-full space-y-4 text-slate-900" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-extrabold text-slate-900">Attach Vendor SLA Contract</h3>
            <form onSubmit={handleCreateContract} className="space-y-3">
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Contract Number / Ref</label>
                <input
                  type="text"
                  placeholder="e.g. LOREAL-SLA-2026"
                  value={contractNumber}
                  onChange={(e) => setContractNumber(e.target.value)}
                  className="lss-input w-full"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full"
                />
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase tracking-wide block mb-1">Terms & Penalty Clause</label>
                <textarea
                  value={terms}
                  onChange={(e) => setTerms(e.target.value)}
                  placeholder="e.g. 5% rebate if delivery exceeds lead time"
                  className="lss-input w-full h-20"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowContractModal(false)}
                  className="lss-btn-outline px-4 py-2 text-xs font-bold uppercase tracking-wider"
                >
                  Cancel
                </button>
                <button type="submit" className="lss-btn-gold px-4 py-2 text-xs font-bold uppercase tracking-wider">
                  Attach Contract
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
